import { Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import { ImpersonationService } from '../../../services/impersonation.service';
import {
  createAuthBroadcastChannel,
  onAuthBroadcastEvent,
  type AuthSyncMessage,
} from '../../session-sync/utils/auth-sync.util';
import { clearSession, clearToken } from '../../session-sync/utils/session-storage.util';
import { AUTH_EVENTS, ONE_YEAR_MS } from '../constants/auth.constants';
import type { IRemoteAuthFacade } from '../interfaces/auth-core.interfaces';
import { AuthSessionStatus, type AuthenticationSession } from '../models/auth-session.model';
import { AUTHENTICATION_ENABLED, AUTH_SHELL_APP_URL } from '../tokens/auth-runtime.token';
import { buildShellAuthenticationRedirectUrl } from '../utils/auth-env.util';
import { AuthStateService } from './auth-state.service';

/** Stub signed-in session emitted when authentication is disabled globally. */
const DISABLED_AUTH_SESSION: AuthenticationSession = {
  status: AuthSessionStatus.SignedIn,
  subjectId: 'auth-disabled',
  issuedAt: new Date().toISOString(),
  expiresAt: new Date(Date.now() + ONE_YEAR_MS).toISOString(),
  lastActivityAt: new Date().toISOString(),
  idleWarningAt: null,
  graceEndsAt: null,
  permissions: [],
  customAccessToken: '',
};

/**
 * Passive auth facade consumed by remote MFEs. Remotes inject this service to read session state — they never initiate
 * login. Subscribes to BroadcastChannel events to stay synchronized with shell.
 *
 * `providedIn: 'root'` is intentional: in Module Federation each remote app has its own Angular root injector, so this
 * yields exactly one instance per remote application. This guarantees a single BroadcastChannel listener regardless of
 * how many components inject the service, preventing duplicate event handling.
 */
@Injectable({ providedIn: 'root' })
export class RemoteAuthFacadeService implements IRemoteAuthFacade, OnDestroy {
  private readonly authState = inject(AuthStateService);
  private readonly impersonationService = inject(ImpersonationService);

  /**
   * When authentication is globally disabled, skip BroadcastChannel wiring entirely — no resources allocated, no
   * session sync attempted.
   */
  private readonly authEnabled = inject(AUTHENTICATION_ENABLED, { optional: true }) ?? true;
  private readonly shellAppUrl = inject(AUTH_SHELL_APP_URL, { optional: true }) ?? window.location.origin;
  private readonly channel = this.authEnabled ? createAuthBroadcastChannel() : null;
  private readonly _sub: Subscription;

  constructor() {
    this._sub = new Subscription();
    if (this.authEnabled && this.channel) {
      onAuthBroadcastEvent(this.channel, (msg: AuthSyncMessage) => this.handleBroadcast(msg));
    }
  }

  /**
   * When auth is disabled, always emit a stub signed-in session so remote templates render immediately without waiting
   * for shell session state.
   */
  readonly session$: Observable<AuthenticationSession> = this.authEnabled
    ? this.authState.session$
    : of(DISABLED_AUTH_SESSION);

  /** Returns true when authentication is disabled or the current session is signed in. */
  isAuthenticated(): boolean {
    return !this.authEnabled || this.authState.getSession().status === 'signed-in';
  }

  /**
   * Remote-side handoff for direct remote URL access while signed out. Remotes do not start login; they redirect to
   * shell where authentication is initiated.
   */
  redirectToShellIfUnauthenticated(returnPath?: string): void {
    if (!this.authEnabled) {
      return;
    }

    const status = this.authState.getSession().status;
    if (status === AuthSessionStatus.SignedIn || status === AuthSessionStatus.SigningIn) {
      return;
    }

    const target = buildShellAuthenticationRedirectUrl(this.shellAppUrl, window.location.href, returnPath);
    if (window.location.href !== target) {
      window.location.assign(target);
    }
  }

  /** Processes shell-originated auth synchronization events for the current tab. */
  private handleBroadcast(msg: AuthSyncMessage): void {
    switch (msg.type) {
      case AUTH_EVENTS.LOGOUT:
      case AUTH_EVENTS.SESSION_EXPIRED:
        // Clear impersonation state first (if any active impersonation)
        // This handles both manual logout and session timeout scenarios
        void this.impersonationService.clearImpersonationStateAndData();
        clearSession();
        clearToken();
        this.authState.updateSession({
          status: AuthSessionStatus.SignedOut,
          subjectId: '',
          issuedAt: '',
          expiresAt: '',
          lastActivityAt: '',
          idleWarningAt: null,
          graceEndsAt: null,
          customAccessToken: '',
          permissions: [],
        });
        // Shell owns interactive redirects; remotes only synchronize local state.
        break;
      case AUTH_EVENTS.IDLE_WARNING:
        this.authState.updateSession({ status: AuthSessionStatus.IdleWarning });
        break;
    }
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    this.channel?.close();
  }
}
